import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wrench } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form.name, form.email, form.password);
      navigate("/app/vehicules?new=1");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-12 bg-background">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="h-10 w-10 rounded-xl bg-blue-600 text-white grid place-items-center">
            <Wrench className="h-5 w-5" />
          </span>
          <span className="font-display font-bold text-xl text-slate-900">Mon Entretien Auto</span>
        </Link>
        <div className="bg-white border border-slate-200 rounded-xl p-7 shadow-sm animate-fade-up">
          <h1 className="font-display text-2xl font-bold text-slate-900">Créer un compte</h1>
          <p className="mt-1 text-sm text-slate-500">Quelques secondes suffisent.</p>
          <form onSubmit={submit} className="mt-7 space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name">Nom</Label>
              <Input id="name" required value={form.name} onChange={set("name")}
                data-testid="register-name-input" placeholder="Jean Dupont" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")}
                data-testid="register-email-input" placeholder="vous@exemple.fr" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe (6 caractères min.)</Label>
              <PasswordInput id="password" required minLength={6} value={form.password}
                onChange={set("password")} testId="register-password-input" placeholder="••••••••" />
            </div>
            {error && (
              <p data-testid="register-error" className="text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <Button type="submit" disabled={loading} data-testid="register-submit-btn"
              className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
              {loading ? "Création…" : "Créer mon compte"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500 text-center">
            Déjà inscrit ?{" "}
            <Link to="/connexion" data-testid="go-login-link" className="font-semibold text-blue-600 hover:text-blue-700">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
